<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import Header from './component/header.vue'

const store      = useStore()
const permission = computed(() => store.state.getCurrentUserPermission)

const loading      = ref(false)
const saving       = ref(false)
const competencies = ref([])
const types        = ref([])
const showModal    = ref(false)
const editId       = ref(null)
const formError    = ref('')

const blankForm = () => ({
  title: '', description: '', type: '', weight: '',
})
const form = ref(blankForm())

const load = async () => {
  try {
    loading.value = true
    const res = await axios.get('/hr/pms/competencies')
    competencies.value = res.data.data?.data ?? res.data.data ?? []
  } catch (e) { console.error(e) } finally { loading.value = false }
}

const loadTypes = async () => {
  try {
    const res = await axios.get('/hr/pms/competencies/types')
    types.value = res.data.data ?? []
  } catch (e) { console.error(e) }
}

onMounted(() => { load(); loadTypes() })

const openCreate = () => {
  editId.value = null; form.value = blankForm(); formError.value = ''; showModal.value = true
}

const openEdit = (c) => {
  editId.value = c.id
  form.value = {
    title: c.title, description: c.description ?? '',
    type: c.type ?? '', weight: c.weight ?? '',
  }
  formError.value = ''; showModal.value = true
}

const save = async () => {
  if (!form.value.title) { formError.value = 'Competency title is required.'; return }
  try {
    saving.value = true; formError.value = ''
    if (editId.value) {
      await axios.put(`/hr/pms/competencies/${editId.value}`, form.value)
    } else {
      await axios.post('/hr/pms/competencies', form.value)
    }
    showModal.value = false; load()
  } catch (e) {
    formError.value = e.response?.data?.message ?? 'Failed to save'
  } finally { saving.value = false }
}

const deleteComp = async (id) => {
  if (!confirm('Delete this competency?')) return
  await axios.delete(`/hr/pms/competencies/${id}`)
  load()
}

const typeLabel = (t) => ({ core: 'Core', functional: 'Functional', leadership: 'Leadership' }[t] ?? t)

const grouped = computed(() => {
  const map = {}
  for (const c of competencies.value) {
    const key = c.type || 'General'
    if (!map[key]) map[key] = []
    map[key].push(c)
  }
  // Sort: core → functional → leadership → others
  const order = ['core', 'functional', 'leadership']
  return Object.fromEntries(
    Object.entries(map).sort(([a], [b]) => {
      const ai = order.indexOf(a), bi = order.indexOf(b)
      if (ai === -1 && bi === -1) return a.localeCompare(b)
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })
  )
})
</script>

<template>
  <div>
    <Header />
    <div class="page">
      <div class="toolbar">
        <h2 class="page-title">Competency Framework</h2>
        <button v-if="permission?.manage_pms_competencies" class="btn-primary" @click="openCreate">+ New Competency</button>
      </div>

      <div v-if="loading" class="empty">Loading…</div>
      <div v-else-if="!competencies.length" class="empty">No competencies defined yet.</div>
      <template v-else>
        <div v-for="(items, cat) in grouped" :key="cat" class="group-section">
          <h3 class="group-title">{{ typeLabel(cat) }}</h3>
          <div class="comp-grid">
            <div v-for="c in items" :key="c.id" class="comp-card">
              <div class="comp-name">{{ c.title }}</div>
              <div v-if="c.weight" class="comp-weight">Weight: {{ c.weight }}%</div>
              <div v-if="c.description" class="comp-desc">{{ c.description }}</div>
              <div class="card-actions">
                <button v-if="permission?.manage_pms_competencies" class="btn-link" @click="openEdit(c)">Edit</button>
                <button v-if="permission?.manage_pms_competencies" class="btn-link btn-link--red" @click="deleteComp(c.id)">Delete</button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>{{ editId ? 'Edit' : 'New' }} Competency</h3>
          <button class="modal-close" @click="showModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <div class="form-grid">
            <div class="form-group form-group--full">
              <label>Competency Title *</label>
              <input v-model="form.title" placeholder="e.g. Communication" />
            </div>
            <div class="form-group">
              <label>Type / Category</label>
              <select v-model="form.type">
                <option value="">— Select type —</option>
                <option v-for="t in types" :key="t" :value="t">{{ typeLabel(t) }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>Weight (%)</label>
              <input type="number" v-model.number="form.weight" min="0" max="100" step="0.5" placeholder="0" />
            </div>
            <div class="form-group form-group--full">
              <label>Description</label>
              <textarea v-model="form.description" rows="2" placeholder="Describe the competency…" />
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn-outline" @click="showModal = false">Cancel</button>
            <button class="btn-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page { padding: 0 24px 40px; }
.toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
.page-title { font-size: 20px; font-weight: 700; color: #1a2a45; margin: 0; }
.empty { text-align: center; color: #888; padding: 60px; font-size: 14px; }
.group-section { margin-bottom: 28px; }
.group-title { font-size: 14px; font-weight: 700; color: #556; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 12px; }
.comp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 14px; }
.comp-card { background: #fff; border: 1px solid #eaf0fb; border-radius: 10px; padding: 16px 18px; }
.comp-name   { font-size: 14px; font-weight: 700; color: #1a2a45; margin-bottom: 4px; }
.comp-weight { font-size: 12px; color: #888; margin-bottom: 6px; }
.comp-desc   { font-size: 12.5px; color: #888; margin-bottom: 8px; }
.card-actions { display: flex; gap: 8px; border-top: 1px solid #f0f3fa; padding-top: 8px; margin-top: 6px; }
.btn-primary  { padding: 9px 20px; background: #004AAD; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-outline  { padding: 9px 20px; background: #fff; color: #004AAD; border: 1px solid #004AAD; border-radius: 6px; font-size: 13.5px; cursor: pointer; }
.btn-link     { background: none; border: none; color: #004AAD; font-size: 13px; cursor: pointer; padding: 0 4px; }
.btn-link--red { color: #dc3545; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding-top: 60px; }
.modal-box     { background: #fff; border-radius: 12px; width: 560px; max-width: 95vw; max-height: 85vh; overflow-y: auto; box-shadow: 0 8px 40px rgba(0,0,0,0.18); }
.modal-header  { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eaf0fb; }
.modal-header h3 { font-size: 16px; font-weight: 700; color: #1a2a45; margin: 0; }
.modal-close   { background: none; border: none; font-size: 22px; color: #888; cursor: pointer; }
.modal-body    { padding: 20px 24px 24px; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.form-grid     { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 18px; }
.form-group    { display: flex; flex-direction: column; gap: 5px; }
.form-group--full { grid-column: 1 / -1; }
.form-group label { font-size: 12.5px; font-weight: 600; color: #556; }
.form-group input, .form-group select, .form-group textarea { padding: 9px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; color: #1a2a45; resize: vertical; }
.form-error { background: #f8d7da; color: #842029; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 14px; }
</style>
